import type { NewOrderFormSchemaInterface } from '~pages/Sales/Orders/newOrderFormSchema';

export type FieldMetadata = {
  /** i18n key for the field's user-facing label. */
  label: string;
  /** i18n key for the field's longer description. */
  description: string;
  /** Free-form English copy consumed directly by AI surfaces (no translation). */
  aiHint: string;
  /**
   * The orderFormV3 file that renders (or, for fields no panel renders —
   * defaults-seeded ids, effect-written values — owns) this field. Embedded
   * legacy widgets keep their own internals; `panel` then names the panel
   * that mounts the embed.
   */
  panel: string;
  /**
   * `order-form-v3-<schema-key-kebab-cased>` for fields a panel renders
   * directly; `null` when the field renders inside an embedded legacy widget
   * (the embed's existing selectors apply) or has no rendered widget.
   */
  testId: string | null;
};

// The `satisfies` clause is the AI-native exhaustiveness gate: adding a key
// to NewOrderFormSchemaInterface without adding metadata here
// fails compilation. Labels and descriptions are i18n keys (labels reuse the
// existing order.form.* / form_fields.* catalog); aiHint is free-form English
// copy that AI surfaces (voice prefill, email extraction, sidebar drawer)
// consume directly without further translation.
export const orderFieldMetadata = {
  id: {
    label: 'form_fields.id',
    description: 'order.form.id.description',
    aiHint:
      'Internal Tread order identifier. AI agents should not set this — it is assigned by the BE.',
    panel: 'OrderFormV3Context.tsx',
    testId: null,
  },
  company: {
    label: 'form_fields.company',
    description: 'order.form.company.description',
    aiHint:
      'The Tread company creating this order. Selectable on create only; hidden when editing an existing order.',
    panel: 'OrderCustomerDetailsPanel.tsx',
    testId: 'order-form-v3-company',
  },
  jobStartAt: {
    label: 'order.form.job_start_date',
    description: 'order.form.job_start_at.description',
    aiHint:
      'ISO datetime when jobs under this order start. Kept in sync with loadAt while "same as order start" is checked.',
    panel: 'DateAndTimePanel.tsx',
    testId: null,
  },
  loadAt: {
    label: 'form_fields.load_at',
    description: 'order.form.load_at.description',
    aiHint:
      'ISO datetime when the order starts loading. A past value on a new order raises a warning but is allowed.',
    panel: 'DateAndTimePanel.tsx',
    testId: null,
  },
  orderId: {
    label: 'form_fields.order_id',
    description: 'order.form.order_id.description',
    aiHint:
      'Human-readable order reference (e.g. PED-241). Assigned by the BE; never set it.',
    panel: 'OrderFormV3Context.tsx',
    testId: null,
  },
  notes: {
    label: 'form_fields.notes',
    description: 'order.form.notes.description',
    aiHint:
      'Legacy notes blob kept for back-compat. Prefer orderNotes / jobNotes / internalNotes / projectNotes.',
    panel: 'NotesPanel.tsx',
    testId: null,
  },
  jobNotes: {
    label: 'form_fields.driver_notes_placeholder',
    description: 'order.form.job_notes.description',
    aiHint: 'Notes shown to drivers on every job created under this order.',
    panel: 'NotesPanel.tsx',
    testId: null,
  },
  projectNotes: {
    label: 'form_fields.project_notes_placeholder',
    description: 'order.form.project_notes.description',
    aiHint:
      'Read-only mirror of the parent project notes; populated by the project cascade.',
    panel: 'NotesPanel.tsx',
    testId: null,
  },
  orderNotes: {
    label: 'form_fields.order_notes_placeholder',
    description: 'order.form.order_notes.description',
    aiHint: 'Free-form notes visible at the order level.',
    panel: 'NotesPanel.tsx',
    testId: null,
  },
  internalNotes: {
    label: 'form_fields.internal_notes',
    description: 'order.form.internal_notes.description',
    aiHint: 'Notes visible only to the owning company; never shared with vendors.',
    panel: 'NotesPanel.tsx',
    testId: null,
  },
  jobTime: {
    label: 'form_fields.job_time',
    description: 'order.form.job_time.description',
    aiHint:
      'Estimated job duration in hours. Stored as a number; the BE persists minutes (UI divides by 60 on load).',
    panel: 'LocationDetailsPanel.tsx',
    testId: null,
  },
  truckCount: {
    label: 'form_fields.truck_count',
    description: 'order.form.truck_count.description',
    aiHint:
      'Number of trucks requested (1-200). On persisted orders this reflects the jobs count and is not editable.',
    panel: 'TrucksPanel.tsx',
    testId: null,
  },
  truckDelay: {
    label: 'form_fields.truck_delay',
    description: 'order.form.truck_delay.description',
    aiHint: 'Stagger between truck dispatches, in minutes.',
    panel: 'DateAndTimePanel.tsx',
    testId: null,
  },
  trucksPerDelay: {
    label: 'form_fields.trucks_per_stagger',
    description: 'order.form.trucks_per_delay.description',
    aiHint: 'How many trucks leave per stagger interval. Must not exceed truckCount.',
    panel: 'DateAndTimePanel.tsx',
    testId: null,
  },
  loadsPerTruck: {
    label: 'form_fields.loads_per_truck',
    description: 'order.form.loads_per_truck.description',
    aiHint:
      'Optional cap on loads per truck. Maximum depends on the vendorJobSplittingEnabled flag.',
    panel: 'TrucksPanel.tsx',
    testId: null,
  },
  unitOfMeasure: {
    label: 'form_fields.unit_of_measure',
    description: 'order.form.unit_of_measure.description',
    aiHint:
      'Unit for the order quantity ({ id, name }). Required unless the service is Hired Truck with an empty quantity.',
    panel: 'MaterialsPanel.tsx',
    testId: null,
  },
  account: {
    label: 'form_fields.account',
    description: 'order.form.account.description',
    aiHint:
      'The customer account this order bills to. Match against existing accounts by name; always required.',
    panel: 'OrderCustomerDetailsPanel.tsx',
    testId: null,
  },
  project: {
    label: 'form_fields.project',
    description: 'order.form.project.description',
    aiHint:
      'Optional parent project ({ id, name }). Selecting one cascades ~12 defaults into the order.',
    panel: 'OrderCustomerDetailsPanel.tsx',
    testId: null,
  },
  material: {
    label: 'form_fields.material',
    description: 'order.form.material.description',
    aiHint:
      'Material being hauled ({ id, name }). Required unless the service is Hired Truck.',
    panel: 'MaterialsPanel.tsx',
    testId: null,
  },
  equipmentType: {
    label: 'form_fields.equipment_type',
    description: 'order.form.equipment_type.description',
    aiHint:
      'Requested equipment type ({ id, name }). Required when the tomlinsonOrderModal flag is on.',
    panel: 'TrucksPanel.tsx',
    testId: null,
  },
  service: {
    label: 'order.form.service',
    description: 'order.form.service.description',
    aiHint:
      'Order service ({ id, name, type }); defaults to the Hired Truck service on new orders via a Provider effect. Drives conditional validation across material, quantity, and waypoints.',
    panel: 'OrderFormV3Context.tsx',
    testId: null,
  },
  quantity: {
    label: 'form_fields.quantity',
    description: 'order.form.quantity.description',
    aiHint:
      'Ordered quantity (number). Optional for Hired Truck / Standing Haul services, required otherwise.',
    panel: 'MaterialsPanel.tsx',
    testId: null,
  },
  state: {
    label: 'form_fields.state',
    description: 'order.form.state.description',
    aiHint:
      'Order lifecycle state (e.g. created, completed). Server-managed; do not set.',
    panel: 'OrderFormV3Context.tsx',
    testId: null,
  },
  pickUpWayPoint: {
    label: 'form_fields.pick_up_site',
    description: 'order.form.pick_up_way_point.description',
    aiHint:
      'Pickup waypoint (site + optional contact + ordinality). Required unless the service is Hired Truck.',
    panel: 'LocationDetailsPanel.tsx',
    testId: null,
  },
  dropOffWayPoint: {
    label: 'form_fields.drop_off_site',
    description: 'order.form.drop_off_way_point.description',
    aiHint:
      'Drop-off waypoint (site + optional contact + ordinality). Required unless the service is Hired Truck.',
    panel: 'LocationDetailsPanel.tsx',
    testId: null,
  },
  stagingWayPoint: {
    label: 'form_fields.staging_site',
    description: 'order.form.staging_way_point.description',
    aiHint: 'Optional intermediate staging waypoint.',
    panel: 'LocationDetailsPanel.tsx',
    testId: null,
  },
  phase: {
    label: 'order.form.phase_selection',
    description: 'order.form.phase.description',
    aiHint:
      'Selected project phase ({ id, name, code }). Managed inside the embedded ProjectDetailsV2 widget.',
    panel: 'ProjectDetailsPanel.tsx',
    testId: null,
  },
  phaseName: {
    label: 'form_fields.phase_name',
    description: 'order.form.phase_name.description',
    aiHint:
      'Phase name string; required when the tomlinsonOrderModal flag is on. Written by phase selection.',
    panel: 'ProjectDetailsPanel.tsx',
    testId: null,
  },
  phaseCode: {
    label: 'form_fields.phase_code',
    description: 'order.form.phase_code.description',
    aiHint:
      'Phase code string; required when the tomlinsonOrderModal flag is on. Written by phase selection.',
    panel: 'ProjectDetailsPanel.tsx',
    testId: null,
  },
  foremen: {
    label: 'form_fields.foreman',
    description: 'order.form.foremen.description',
    aiHint: 'User ids of foremen assigned to this order.',
    panel: 'ContactsPanel.tsx',
    testId: null,
  },
  collaborators: {
    label: 'form_fields.collaborator',
    description: 'order.form.collaborators.description',
    aiHint: 'User ids of internal collaborators on this order.',
    panel: 'ContactsPanel.tsx',
    testId: null,
  },
  salespeople: {
    label: 'form_fields.sales',
    description: 'order.form.salespeople.description',
    aiHint: 'User ids of salespeople attached to this order.',
    panel: 'ContactsPanel.tsx',
    testId: null,
  },
  allContacts: {
    label: 'order.form.contacts',
    description: 'order.form.all_contacts.description',
    aiHint:
      'List of order contacts (name + phone + type). A type is required once a name or phone is provided.',
    panel: 'ContactsPanel.tsx',
    testId: null,
  },
  projectMaterialTotals: {
    label: 'form_fields.project_materials',
    description: 'order.form.project_material_totals.description',
    aiHint:
      'Read-only material totals copied from the parent project; rendered as disabled rows. Do not edit.',
    panel: 'ProjectDetailsPanel.tsx',
    testId: 'order-form-v3-project-material-totals',
  },
  materialTotals: {
    label: 'form_fields.materials',
    description: 'order.form.material_totals.description',
    aiHint:
      "The order's editable list of materials beyond the primary material, each with its own quantity and unit ({ material, totalQuantity, unitOfMeasure }). Seeded from the project's distinct materials at blank quantity; rows left blank are dropped on submit.",
    panel: 'MaterialsPanel.tsx',
    testId: null,
  },
  allPhaseCodes: {
    label: 'form_fields.phase_code',
    description: 'order.form.all_phase_codes.description',
    aiHint:
      'All phases available on the parent project; populated by the project cascade for phase selection.',
    panel: 'ProjectDetailsPanel.tsx',
    testId: null,
  },
  department: {
    label: 'order.form.department',
    description: 'order.form.department.description',
    aiHint:
      'Internal department owning the order; defaults from the account or project cascade.',
    panel: 'ProjectDetailsPanel.tsx',
    testId: null,
  },
  name: {
    label: 'order.form.order_name',
    description: 'order.form.name.description',
    aiHint:
      'Free-text order name. Required when the require_order_name company setting is on.',
    panel: 'OrderCustomerDetailsPanel.tsx',
    testId: null,
  },
  poJobNumber: {
    label: 'form_fields.po_job_number',
    description: 'order.form.po_job_number.description',
    aiHint: 'Customer purchase-order or job number associated with the order.',
    panel: 'OrderCustomerDetailsPanel.tsx',
    testId: null,
  },
  zone: {
    label: 'order.form.order_zone',
    description: 'order.form.zone.description',
    aiHint: 'Free-text zone reference shown on dispatch surfaces.',
    panel: 'OrderCustomerDetailsPanel.tsx',
    testId: null,
  },
  externalId: {
    label: 'order.form.external_id',
    description: 'order.form.external_id.description',
    aiHint: 'Customer-side or ERP-side identifier for this order. Free-text.',
    panel: 'OrderCustomerDetailsPanel.tsx',
    testId: null,
  },
  serviceClass: {
    label: 'form_fields.service_class',
    description: 'order.form.service_class.description',
    aiHint:
      'Service classification ({ id, name }). Visible when the show_service_class company setting is on; required when the require_order_service_class company setting is also on.',
    panel: 'OrderCustomerDetailsPanel.tsx',
    testId: null,
  },
  equipmentTypeGrossCapacityUnitOfMeasure: {
    label: 'form_fields.unit_of_measure',
    description: 'order.form.equipment_type_gross_capacity_unit_of_measure.description',
    aiHint:
      'Unit-of-measure paired with equipmentTypeGrossCapacity ({ id, name }). Required once a capacity is set.',
    panel: 'TrucksPanel.tsx',
    testId: null,
  },
  equipmentTypeGrossCapacity: {
    label: 'form_fields.gross_capacity',
    description: 'order.form.equipment_type_gross_capacity.description',
    aiHint: 'Gross capacity (number-string) for the chosen equipment type.',
    panel: 'TrucksPanel.tsx',
    testId: null,
  },
  loadUnitOfMeasure: {
    label: 'form_fields.load_unit',
    description: 'order.form.load_unit_of_measure.description',
    aiHint:
      'Unit-of-measure used when billing per load. No order-form widget edits it; it round-trips from the saved order.',
    panel: 'OrderFormV3Context.tsx',
    testId: null,
  },
  unitsPerHour: {
    label: 'form_fields.units_per_hour',
    description: 'order.form.units_per_hour.description',
    aiHint:
      'Estimated throughput; written by the TrucksV2 estimate effects, displayed in delivery estimates.',
    panel: 'TrucksPanel.tsx',
    testId: null,
  },
  jobUnitOfMeasure: {
    label: 'form_fields.unit_of_measure',
    description: 'order.form.job_unit_of_measure.description',
    aiHint:
      'Unit-of-measure for jobQuantity ({ id, name }). When it is Load, jobQuantity becomes optional.',
    panel: 'TrucksPanel.tsx',
    testId: null,
  },
  jobQuantity: {
    label: 'form_fields.job_quantity',
    description: 'order.form.job_quantity.description',
    aiHint: 'Per-job quantity. Required (min 0.01) unless jobUnitOfMeasure is Load.',
    panel: 'TrucksPanel.tsx',
    testId: null,
  },
  contractNumber: {
    label: 'order.form.contract_number',
    description: 'order.form.contract_number.description',
    aiHint:
      'Contract reference number; the field inside the embed is gated by its internal show_contract_number company setting.',
    panel: 'OrderCustomerDetailsPanel.tsx',
    testId: null,
  },
  dispatchNumber: {
    label: 'order.form.dispatch_number',
    description: 'order.form.dispatch_number.description',
    aiHint: 'Dispatch reference label shown to drivers and on tickets.',
    panel: 'OrderCustomerDetailsPanel.tsx',
    testId: null,
  },
  fileAttachments: {
    label: 'form_fields.order_attachments',
    description: 'order.form.file_attachments.description',
    aiHint:
      'Uploaded file attachments scoped to this order. Managed by the FileAttachments embed via its handler ref.',
    panel: 'FileAttachmentsPanel.tsx',
    testId: null,
  },
  requester: {
    label: 'order.form.requester',
    description: 'order.form.requester.description',
    aiHint:
      'Single user from the order company who requested the order. Defaults to the current user on create.',
    panel: 'RequesterPanel.tsx',
    testId: 'order-form-v3-requester',
  },
  estimatedDeliveredQuantity: {
    label: 'form_fields.estimation',
    description: 'order.form.estimated_delivered_quantity.description',
    aiHint:
      'Estimated delivered quantity surfaced in delivery estimates. Effect-written; do not set directly.',
    panel: 'DeliveryEstimatesPanel.tsx',
    testId: null,
  },
  cycleTime: {
    label: 'form_fields.load_cycle_avg',
    description: 'order.form.cycle_time.description',
    aiHint:
      'Full cycle time in minutes; derived from map geometry by RouteEstimateEffects unless overridden.',
    panel: 'LocationDetailsPanel.tsx',
    testId: null,
  },
  cycleDistance: {
    label: 'form_fields.distance',
    description: 'order.form.cycle_distance.description',
    aiHint:
      'Pickup-to-dropoff distance in the company distance unit; derived from map geometry by RouteEstimateEffects.',
    panel: 'LocationDetailsPanel.tsx',
    testId: null,
  },
  pickupOnsite: {
    label: 'form_fields.pick_up',
    description: 'order.form.pickup_onsite.description',
    aiHint: 'Minutes a truck spends on-site at pickup. Defaults from the company.',
    panel: 'LocationDetailsPanel.tsx',
    testId: null,
  },
  dropoffOnsite: {
    label: 'form_fields.drop_off',
    description: 'order.form.dropoff_onsite.description',
    aiHint: 'Minutes a truck spends on-site at drop-off. Defaults from the company.',
    panel: 'LocationDetailsPanel.tsx',
    testId: null,
  },
  pickupToDropoff: {
    label: 'form_fields.time',
    description: 'order.form.pickup_to_dropoff.description',
    aiHint: 'Travel minutes from pickup to drop-off; part of the cycle estimate.',
    panel: 'LocationDetailsPanel.tsx',
    testId: null,
  },
  totalLoads: {
    label: 'order.form.loads',
    description: 'order.form.total_loads.description',
    aiHint:
      'Expected total loads; written by the TrucksV2 estimate effects, displayed in delivery estimates.',
    panel: 'TrucksPanel.tsx',
    testId: null,
  },
  recurringLoads: {
    label: 'form_fields.recurring',
    description: 'order.form.recurring_loads.description',
    aiHint:
      'Toggle indicating the order runs recurring loads. Defaults from the running_default_on company setting.',
    panel: 'TrucksPanel.tsx',
    testId: null,
  },
  chaseMissingTickets: {
    label: 'order.form.text_drivers_for_missing_tickets',
    description: 'order.form.chase_missing_tickets.description',
    aiHint:
      'Toggle gated by the driverAgent flag: text drivers about missing tickets. Defaults from the chase_missing_tickets_default_on company setting.',
    panel: 'TrucksPanel.tsx',
    testId: 'order-form-v3-chase-missing-tickets',
  },
  liveEta: {
    label: 'order.form.enable_live_eta',
    description: 'order.form.enable_live_eta.description',
    aiHint:
      'Toggle gated by the liveEtaEnabled FF: opt this order into Dispatch Live ETA. Seeded from the selected project on new orders, else from the live_eta_default company setting; a later project-level change overwrites it (BE-authoritative cascade).',
    panel: 'TrucksPanel.tsx',
    testId: 'order-form-v3-live-eta',
  },
  movingSiteSyncEnabled: {
    label: 'order.form.moving_site_sync_enabled',
    description: 'order.form.moving_site_sync_enabled.description',
    aiHint:
      'Toggle: let PaverTracker telematics attach or move Daily sites on this order. Defaults on; the embedded checkbox only renders when the company has a PaverTracker fleet connected. Turn off to keep a manually-selected dropoff site from being overwritten.',
    panel: 'LocationDetailsPanel.tsx',
    testId: null,
  },
  defaultDigitalTicketView: {
    label: 'order.form.default_digital_ticket_view',
    description: 'order.form.default_digital_ticket_view.description',
    aiHint:
      'Toggle gated by the show_default_digital_ticket_view company setting: jobs under this order open the digital ticket view by default.',
    panel: 'TrucksPanel.tsx',
    testId: 'order-form-v3-default-digital-ticket-view',
  },
  jobsSplittable: {
    label: 'order.form.allow_vendors_to_split_jobs',
    description: 'order.form.jobs_splittable.description',
    aiHint:
      'Toggle gated by the vendorJobSplittingEnabled flag: vendors may split jobs under this order.',
    panel: 'TrucksPanel.tsx',
    testId: 'order-form-v3-jobs-splittable',
  },
  prevailingWage: {
    label: 'order.form.prevailing_wage',
    description: 'order.form.prevailing_wage.description',
    aiHint: 'Toggle indicating the order is subject to prevailing-wage requirements.',
    panel: 'TrucksPanel.tsx',
    testId: 'order-form-v3-prevailing-wage',
  },
  taxRateSchedule: {
    label: 'order.form.tax_rate_schedule',
    description: 'order.form.tax_rate_schedule.description',
    aiHint:
      'Tax-rate-schedule reference ({ label, value }). Selecting one auto-populates taxRate via a Provider effect.',
    panel: 'TaxPanel.tsx',
    testId: 'order-form-v3-tax-rate-schedule',
  },
  taxRate: {
    label: 'order.form.tax_rate',
    description: 'order.form.tax_rate.description',
    aiHint:
      'Effective tax rate (0-100). Auto-populated from the selected taxRateSchedule.',
    panel: 'TaxPanel.tsx',
    testId: 'order-form-v3-tax-rate',
  },
  taxFreight: {
    label: 'order.form.taxable_freight',
    description: 'order.form.tax_freight.description',
    aiHint: 'Toggle: apply the tax rate to freight charges.',
    panel: 'TaxPanel.tsx',
    testId: 'order-form-v3-tax-freight',
  },
  taxMaterial: {
    label: 'order.form.taxable_material',
    description: 'order.form.tax_material.description',
    aiHint: 'Toggle: apply the tax rate to material charges.',
    panel: 'TaxPanel.tsx',
    testId: 'order-form-v3-tax-material',
  },
  taxAddOns: {
    label: 'order.form.taxable_add_ons',
    description: 'order.form.tax_add_ons.description',
    aiHint: 'Toggle: apply the tax rate to add-on charges.',
    panel: 'TaxPanel.tsx',
    testId: 'order-form-v3-tax-add-ons',
  },
  haulerRateType: {
    label: 'form_fields.hauler_rate_type',
    description: 'order.form.hauler_rate_type.description',
    aiHint:
      'Rate type label/value pair (e.g. Per Load / rate_per_load). Required — and the card only renders — when the tomlinsonOrderModal flag is on.',
    panel: 'RateDetailsPanel.tsx',
    testId: 'order-form-v3-hauler-rate-type',
  },
  materialRate: {
    label: 'form_fields.material_rate',
    description: 'order.form.material_rate.description',
    aiHint: 'Material-rate typeahead selection driving the material-rate block.',
    panel: 'MaterialsPanel.tsx',
    testId: null,
  },
  materialRateUoM: {
    label: 'form_fields.unit',
    description: 'order.form.material_rate_uom.description',
    aiHint:
      'Unit-of-measure for the material rate ({ id, name }). Required once a sell price is set.',
    panel: 'MaterialsPanel.tsx',
    testId: null,
  },
  materialRateBuyPrice: {
    label: 'form_fields.material_rate_buy_price',
    description: 'order.form.material_rate_buy_price.description',
    aiHint: 'Material buy price (number, min 0).',
    panel: 'MaterialsPanel.tsx',
    testId: null,
  },
  materialRateSellPrice: {
    label: 'form_fields.material_rate_sell_price',
    description: 'order.form.material_rate_sell_price.description',
    aiHint:
      'Material sell price (number, min 0). Setting it makes materialRateUoM required.',
    panel: 'MaterialsPanel.tsx',
    testId: null,
  },
  vendorPayCommissionPercentage: {
    label: 'pay_commission.percentage_label',
    description: 'pay_commission.share_hint',
    aiHint:
      'Commission deducted from gross vendor pay; zero suppresses inheritance and null restores it.',
    panel: 'OrderFormV3Context.tsx',
    testId: null,
  },
  driverPayCommissionPercentage: {
    label: 'pay_commission.percentage_label',
    description: 'pay_commission.share_hint',
    aiHint:
      'Commission deducted from gross driver pay; zero suppresses inheritance and null restores it.',
    panel: 'OrderFormV3Context.tsx',
    testId: null,
  },
  materialRateItemized: {
    label: 'form_fields.itemize_material_rate',
    description: 'order.form.material_rate_itemized.description',
    aiHint: 'Toggle: itemize the material rate on invoices.',
    panel: 'MaterialsPanel.tsx',
    testId: null,
  },
  vendorAddOnAssignments: {
    label: 'order.form.vendor_rate_card',
    description: 'order.form.vendor_add_on_assignments.description',
    aiHint:
      'Vendor add-on assignments extracted from FreightRates at submit time. Side-channel; do not set directly.',
    panel: 'TrucksPanel.tsx',
    testId: null,
  },
  customerAddOnAssignments: {
    label: 'order.form.customer_rate_card',
    description: 'order.form.customer_add_on_assignments.description',
    aiHint:
      'Customer add-on assignments extracted from FreightRates at submit time. Side-channel; do not set directly.',
    panel: 'TrucksPanel.tsx',
    testId: null,
  },
  driverAddOnAssignments: {
    label: 'order.form.driver_rate_card',
    description: 'order.form.driver_add_on_assignments.description',
    aiHint:
      'Driver add-on assignments extracted from FreightRates at submit time. Side-channel; do not set directly.',
    panel: 'TrucksPanel.tsx',
    testId: null,
  },
  geofenceDelaySeconds: {
    label: 'form_fields.geo_fence_delay',
    description: 'order.form.geofence_delay_seconds.description',
    aiHint: 'Geofence delay in seconds; seeded from the company default on new orders.',
    panel: 'LocationDetailsPanel.tsx',
    testId: 'order-form-v3-geofence-delay-seconds',
  },
  chargeToAccount: {
    label: 'order.form.charge_to_account',
    description: 'order.form.charge_to_account.description',
    aiHint: 'Optional alternate billing account reference.',
    panel: 'OrderCustomerDetailsPanel.tsx',
    testId: null,
  },
  defaultSiteRateScheduleId: {
    label: 'order.form.haul_rate_card',
    description: 'order.form.default_site_rate_schedule_id.description',
    aiHint:
      'Id of the site-rate-schedule applied by default; managed by the FreightRates SRS flow. Do not set directly.',
    panel: 'TrucksPanel.tsx',
    testId: null,
  },
} as const satisfies Record<keyof NewOrderFormSchemaInterface, FieldMetadata>;

/**
 * The card inventory as ordered panel FILE names per column — the STATIC
 * card set (conditional cards included: the RateDetailsPanel card needs the
 * tomlinsonOrderModal flag, ProjectAttachmentsPanel a persisted project). Names match the `panel`
 * values above, so `orderFieldMetadata[key].panel` always resolves into this
 * inventory (or 'OrderFormV3Context.tsx' for Provider-owned keys).
 * `window.__orderFormV3__.getPanelInventory()` exposes it at runtime; the
 * ordered-inventory test pins the rendered DOM against the same order.
 */
export const orderFormV3PanelInventory = {
  left: [
    'OrderCustomerDetailsPanel.tsx',
    'DateAndTimePanel.tsx',
    'MaterialsPanel.tsx',
    'TrucksPanel.tsx',
    'TaxPanel.tsx',
    'RateDetailsPanel.tsx',
    'DeliveryEstimatesPanel.tsx',
    'NotesPanel.tsx',
  ],
  right: [
    'ProjectDetailsPanel.tsx',
    'LocationDetailsPanel.tsx',
    'ContactsPanel.tsx',
    'FileAttachmentsPanel.tsx',
    'ProjectAttachmentsPanel.tsx',
    'RequesterPanel.tsx',
  ],
} as const;
